/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  BoundingBoxAnnotation,
  ClientCoordinate,
  LineAnnotation,
  LineAnnotationHoverState,
  PointAnnotation,
  SegmentationAnnotation,
  XYCoordinate,
} from '../types';

const MIN_INTERACTION_STROKE_WIDTH = 7;

export const getClientCoordinatesOnCanvas = (
  event: ClientCoordinate,
  scale: number,
  mousePointOffset: DOMRect,
): XYCoordinate => {
  const { clientX, clientY } = event;
  const x = (clientX - mousePointOffset.left) / scale;
  const y = (clientY - mousePointOffset.top) / scale;
  return { x, y };
};

/**
 * Calculates the perpendicular distance from a point (x0, y0)
 * to a line segment defined by two points (x1, y1) and (x2, y2).
 * This distance is calculated using the formula for the distance from a point to a line in a 2D plane.
 */
export const distancePointFromLine = (
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) => {
  return (
    Math.abs((x2 - x1) * (y1 - y0) - (x1 - x0) * (y2 - y1)) /
    Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
  );
};

function isPointOnLine(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  width: number,
) {
  // calculate the distance from the point to the infinite line
  const distance = distancePointFromLine(px, py, x1, y1, x2, y2);

  // check if the point is within 'width / 2' distance of the line
  if (distance > width / 2) {
    return false;
  }

  // make sure the point is within the bounding box of the line segment
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  return px >= minX && px <= maxX && py >= minY && py <= maxY;
}

export const distanceBetweenPoints = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) => {
  return {
    xDistance: Math.abs(x1 - x2),
    yDistance: Math.abs(y1 - y2),
  };
};

export const isHoveringOnBoxAnnotation = (
  boundingBoxes: BoundingBoxAnnotation[],
  coordinates: { x: number; y: number },
  padding = 0,
) => {
  const { x: interactionX, y: interactionY } = coordinates;

  return boundingBoxes.find((box) => {
    const { y, x, height, width } = box;
    const totalInteractionWidth = box.styles.strokeWidth + padding;

    const isInteractingWithLeftSide =
      interactionX >= x + width - padding &&
      interactionX <= x + width + totalInteractionWidth &&
      interactionY >= y - padding &&
      interactionY <= y + height + totalInteractionWidth;

    const isInteractingWithRightSide =
      interactionX >= x - padding &&
      interactionX <= x + totalInteractionWidth &&
      interactionY >= y - padding &&
      interactionY <= y + height + totalInteractionWidth;

    const isInteractingWithTopSide =
      interactionX >= x - padding &&
      interactionX <= x + width + totalInteractionWidth &&
      interactionY >= y - padding &&
      interactionY <= y + totalInteractionWidth;

    const isInteractingWithBottomSide =
      interactionX >= x - padding &&
      interactionX <= x + width + totalInteractionWidth &&
      interactionY >= y + height - padding &&
      interactionY <= y + height + totalInteractionWidth;

    const isInteractingWithBox =
      isInteractingWithLeftSide ||
      isInteractingWithRightSide ||
      isInteractingWithTopSide ||
      isInteractingWithBottomSide;

    return isInteractingWithBox;
  });
};

export const isHoveringOnLineAnnotation = (
  lines: LineAnnotation[],
  coordinates: { x: number; y: number },
  padding = 0,
): LineAnnotationHoverState | undefined => {
  const { x: interactionX, y: interactionY } = coordinates;

  for (const line of lines) {
    const { x1, x2, y1, y2, styles } = line;

    const handleWidth =
      styles.strokeWidth + MIN_INTERACTION_STROKE_WIDTH + padding;

    if (styles.showHandles) {
      const isInteractingWithFirstHandle =
        interactionX >= x1 - handleWidth &&
        interactionX <= x1 + handleWidth &&
        interactionY >= y1 - handleWidth &&
        interactionY <= y1 + handleWidth;

      const isInteractingWithSecondHandle =
        interactionX >= x2 - handleWidth &&
        interactionX <= x2 + handleWidth &&
        interactionY >= y2 - handleWidth &&
        interactionY <= y2 + handleWidth;

      const isInteractingWithLine =
        isInteractingWithFirstHandle || isInteractingWithSecondHandle;

      if (isInteractingWithLine) {
        return {
          annotation: line,
          handle: isInteractingWithFirstHandle ? 'first' : 'second',
        };
      }
    }

    const isInteractingWithLine = isPointOnLine(
      interactionX,
      interactionY,
      x1,
      y1,
      x2,
      y2,
      (styles.strokeWidth > MIN_INTERACTION_STROKE_WIDTH
        ? styles.strokeWidth
        : MIN_INTERACTION_STROKE_WIDTH) + padding,
    );

    if (isInteractingWithLine) {
      return {
        annotation: line,
        handle: 'line',
      };
    }
  }
};

export const isHoveringOnPointAnnotation = (
  points: PointAnnotation[],
  coordinates: { x: number; y: number },
  padding = 0,
) => {
  const { x: interactionX, y: interactionY } = coordinates;

  return points.find((point) => {
    const { x, y, styles } = point;
    const totalInteractionWidth = styles.strokeWidth + padding;

    const isInteractingWithPoint =
      interactionX >= x - totalInteractionWidth &&
      interactionX <= x + totalInteractionWidth &&
      interactionY >= y - totalInteractionWidth &&
      interactionY <= y + totalInteractionWidth;

    return isInteractingWithPoint;
  });
};

export const isHoveringOnSegmentationAnnotation = (
  segmentations: SegmentationAnnotation[],
  coordinates: { x: number; y: number },
  padding = 0,
) => {
  const { x: interactionX, y: interactionY } = coordinates;

  // Function to check if a point is inside a polygon using the ray-casting algorithm
  const isPointInPolygon = (
    polygon: number[][],
    x: number,
    y: number,
  ): boolean => {
    let inside = false;
    const len = polygon.length;

    for (let i = 0, j = len - 1; i < len; j = i++) {
      const [xi, yi] = polygon[i]!;
      const [xj, yj] = polygon[j]!;

      // Check if the point is within the y bounds of the polygon segment
      const intersect =
        yi! > y !== yj! > y &&
        x < ((xj! - xi!) * (y - yi!)) / (yj! - yi!) + xi!;

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  };

  // Iterate through segmentations and check if the interaction point is inside the polygon
  return segmentations.find((segmentation) => {
    const { coordinates, styles } = segmentation;
    const expandedPadding = (styles.strokeWidth ?? 0) / 2 + padding;

    // Expand each polygon coordinate by padding
    const expandedPolygon = coordinates.map(([x, y]) => [
      x! - expandedPadding,
      y! - expandedPadding,
    ]);

    return isPointInPolygon(expandedPolygon, interactionX, interactionY);
  });
};
